import React, { useEffect, useState, useRef } from "react";
import Form from "react-bootstrap/Form";

function InputField({
  field,
  label,
  value,
  onChange,
  error,
  type = "text",
  as = "Form.Group",
  pattern = null,
  controlId = "applicant",
  options = [], // Add this for select options
  disabled = false,
  min = "",
  required = true,
  allowCommas = false,
  allowDollar = false,
  placeholder
}) {
  const inputRef = useRef(null);

  const formatDollarValue = (val) => {
    if (!val || val === "0" || val === 0) return "";
    let numericOnly = String(val).replace(/[^\d.]/g, "");
    const parts = numericOnly.split(".");
    let integerPart = parts[0];
    let decimalPart = parts.length > 1 ? "." + parts[1] : "";
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return integerPart || decimalPart ? "$" + integerPart + decimalPart : "";
  };

  const [displayValue, setDisplayValue] = useState(
    allowDollar && value ? formatDollarValue(value) : (value || "")
  );

  useEffect(() => {
    if (allowDollar) {
      setDisplayValue(formatDollarValue(value));
    } else {
      setDisplayValue(value || "");
    }
  }, [value, allowDollar]);

  const calculateNewCursorPosition = (oldValue, newValue, oldCursorPos) => {
    if (!newValue || newValue === "") return 1;
    
    // Remove formatting from both values for comparison
    const oldNumeric = oldValue.replace(/[$,]/g, "");
    const newNumeric = newValue.replace(/[$,]/g, "");
    
    // Count non-numeric characters before cursor in old value
    let nonNumericBeforeCursor = 0;
    for (let i = 0; i < oldCursorPos && i < oldValue.length; i++) {
      if (oldValue[i] === '$' || oldValue[i] === ',') {
        nonNumericBeforeCursor++;
      }
    }
    
    // Calculate which numeric digit position the cursor was at (0-indexed)
    // Subtract 1 if there's a $ at the start
    const hasDollarSign = oldValue[0] === '$';
    const numericPos = Math.max(0, oldCursorPos - nonNumericBeforeCursor - (hasDollarSign ? 1 : 0));
    
    // Handle edge cases
    if (numericPos === 0 && newNumeric.length > 0) {
      // Cursor was at the first digit, position after $
      return 1;
    }
    
    if (numericPos >= newNumeric.length) {
      // Cursor was at or past the end, position at the end
      return newValue.length;
    }
    
    // Find corresponding position in new formatted value
    // We need to find where the numericPos-th digit is in the formatted string
    let numericCount = 0;
    for (let i = 1; i < newValue.length; i++) {
      if (newValue[i] !== ',') {
        if (numericCount === numericPos) {
          // Found the position, place cursor after this digit
          return i + 1;
        }
        numericCount++;
      }
    }
    
    // Fallback: position at the end
    return newValue.length;
  };

  const handleInputChange = (e) => {
    const input = e.target;
    const oldValue = input.value;
    const oldCursorPos = input.selectionStart;
    let newValue = e.target.value;

    if (allowDollar) {
      let numericOnly = newValue.replace(/[^\d.]/g, "");
      const parts = numericOnly.split(".");
      let integerPart = parts[0];
      let decimalPart = parts.length > 1 ? "." + parts[1] : "";
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      newValue = integerPart || decimalPart ? "$" + integerPart + decimalPart : "";
      
      setDisplayValue(newValue);
      
      // Calculate and set cursor position
      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = calculateNewCursorPosition(oldValue, newValue, oldCursorPos);
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    } else if (allowCommas) {
      let numericOnly = newValue.replace(/[^\d.]/g, "");
      const parts = numericOnly.split(".");
      let integerPart = parts[0];
      let decimalPart = parts.length > 1 ? "." + parts[1] : "";
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      newValue = integerPart + decimalPart;
    } else if (pattern === "^[0-9]*$") {
      newValue = newValue.replace(/\D/g, "");
    }

    onChange(field, newValue);
  };

  return (
    <Form.Group as={as} controlId={`${controlId}${field}`}>
      <Form.Label>{label}{required ? <span className="text-danger">*</span> : null}</Form.Label>
      {type === "select" ? ( // Check if type is "select"
        <Form.Select
          value={value}
          onChange={(e) => onChange(field, e.target.value)} // Pass the selected value
          isInvalid={!!error}
        >
          <option value="">Select {label}</option>
          {options.map((option, index) => {
            const optionValue = typeof option === "object" ? option.value : option;
            const optionLabel = typeof option === "object" ? option.label : option;
            return (
              <option key={index} value={String(optionValue)}>
                {optionLabel}
              </option>
            );
          })}
        </Form.Select>
      ) : (
        <Form.Control
          ref={inputRef}
          type={type}
          value={allowDollar ? displayValue : (value || "")}
          onChange={handleInputChange}
          isInvalid={!!error}
          pattern={type === "text" ? pattern : undefined}
          disabled={disabled}
          min={min}
          placeholder={placeholder}
        />
      )}
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
}

export default InputField;
